import { EMPTY, Observable } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AlertNotificationService } from 'src/app/services/alert-notification.service';

/** Extracts the backend error message from an HttpErrorResponse (same envelope used across the app). */
export const apiErrorMessage = (error: any, fallback: string): string =>
  error?.error?.error?.message || error?.error?.status?.description || fallback;

export const isValidationError = (error: any): boolean =>
  error?.error?.error?.description === 'ARGUMENT_VALIDATION_ERROR';

/**
 * Wraps a delete call used by the data-table delete button: shows a success/error toast and
 * swallows the error so that the row is removed only when the backend confirms the deletion.
 */
export const withDeleteFeedback = (
  source$: Observable<any>,
  alertNotificationService: AlertNotificationService,
  entityLabel: string
): Observable<any> =>
  source$.pipe(
    tap(() => alertNotificationService.success(`${entityLabel} deleted successfully`)),
    catchError((error) => {
      alertNotificationService.error(apiErrorMessage(error, `Error deleting ${entityLabel.toLowerCase()}`));
      return EMPTY;
    })
  );

export const toWebsiteHref = (url: string | null | undefined): string | null => {
  if (!url) {
    return null;
  }
  return /^https?:\/\//i.test(url) ? url : `https://${url}`;
};

/** Pretty-prints a JSON string; returns the raw value when it is not valid JSON. */
export const formatJsonPayload = (payload: string | null | undefined): string => {
  if (payload === null || payload === undefined || payload === '') {
    return '';
  }
  try {
    return JSON.stringify(JSON.parse(payload), null, 2);
  } catch {
    return payload;
  }
};

/** Converts an ISO date-time to the value expected by `<input type="datetime-local">` (local time, minutes). */
export const isoToDateTimeLocal = (value: string | null | undefined): string => {
  if (!value) {
    return '';
  }
  const date = new Date(value);
  if (isNaN(date.getTime())) {
    return '';
  }
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/** Converts a `datetime-local` value (local time) to an ISO-8601 UTC string. */
export const dateTimeLocalToIso = (value: string | null | undefined): string | null => {
  if (!value) {
    return null;
  }
  const date = new Date(value);
  return isNaN(date.getTime()) ? null : date.toISOString();
};
